import type React from "react";

import { Box, Typography } from "@mui/material";
import { type TooltipProps } from "recharts";
import { useTheme } from "styled-components";

export interface TooltipContentProps extends TooltipProps<number, string> {
  color?: string;
  stackTotals?: Record<string, number>;
  labelFormatter?: (label: string) => string;
  valueFormatter?: (value: number) => string;
}

export const TooltipContent: React.FC<TooltipContentProps> = ({
  active,
  color,
  label,
  payload,
  stackTotals,
  labelFormatter = l => l,
  valueFormatter = value => value.toString(),
}) => {
  const theme = useTheme();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: `1px solid ${theme.colors.redesign.db20}`,
        borderRadius: 1,
        padding: 1.5,
      }}
    >
      {label && (
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.redesign.db100, mb: 1 }}>
          {labelFormatter(label as string)}
          {stackTotals && (
            <>
              -{" "}
              <span style={{ color: color || theme.colors.redesign.b100 }}>
                {valueFormatter(stackTotals[label as string])}
              </span>
            </>
          )}
        </Typography>
      )}
      {payload.map(entry => (
        <Box key={entry.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: entry.color,
            }}
          />
          <Typography sx={{ fontSize: 14, color: theme.colors.redesign.db100 }}>
            {entry.name}:{" "}
            <span style={{ color: color || theme.colors.redesign.b100 }}>{valueFormatter(Number(entry.value))}</span>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
